import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import deepEqual from 'fast-deep-equal';
import { Heading2, Heading3 } from '../../ui/Typography/Heading';
import { Input, Select, Button } from '../../ui';
import { IconTrash, IconCircledPlus } from '../../ui/Icons';
import { updateConfigOnboardingSettings } from '../../../redux/config/slice';
import { getOrganizations, bulkEditOrganizations } from '../../../redux/organizations/slice';
import { OnboardingNavbar } from '../OnboardingNavbar/OnboardingNavbar';
import { OnboardingProgress } from '../OnboardingProgress/OnboardingProgress';
import { OnboardingActions } from '../OnboardingActions/OnboardingActions';
import { selectUpdateConfigLoading } from '../../../redux/config/selectors';
import {
  selectOrganizationsForOnboarding,
  selectOrganizationsLoading,
  selectBulkEditOrganizationsLoading
} from '../../../redux/organizations/selectors';
import { ONBOARDING_STEPS, ORGANIZATIONS_TYPES } from '../../../consts/consts';
import {
  OnboardingHeaderGroup,
  OnboardingContent,
  OnboardingContentGroup,
  OnboardingContentTitle,
  OnboardingStepWrapper,
  OnboardingContentInfo,
  OnboardingContentGroupText,
  OnboardingFieldWrapper,
  OnboardingFieldErrorText,
  OnboardingOrganizationListHeader,
  OnboardingOrganizationListHeaderItem,
  OnboardingOrganizationListRow,
  OnboardingContentGroupTitle,
  OnboardingContentGroupTitleDescription
} from '../../ui/Layout/OnboardingLayout';
import {
  EMAIL_FORMAT_REGEX,
  trimValue,
  getFieldTouchKey,
  touchAllFields
} from '../../../helpers/onboardingValidation.js';

export const OnboardingStepOrganizationsComponent = ({
  updateConfigOnboardingSettings,
  updateConfigLoading,
  organizations,
  organizationsLoading,
  bulkEditOrganizationsLoading,
  getOrganizations,
  bulkEditOrganizations
}) => {
  const [localOrganizations, setLocalOrganizations] = useState([]);
  const [initialOrganizationsState, setInitialOrganizationsState] = useState([]);
  const [touchedFields, setTouchedFields] = useState({});

  useEffect(() => {
    getOrganizations();
  }, [getOrganizations]);

  useEffect(() => {
    if (!organizations) return;
    if (deepEqual(organizations, initialOrganizationsState)) return;

    setLocalOrganizations(organizations);
    setInitialOrganizationsState(organizations);
  }, [organizations, initialOrganizationsState]);

  const filteredOrganizationTypeOptions = ORGANIZATIONS_TYPES.filter((type) => type.canBeCreated).map((type) => ({
    label: type.label,
    value: type.value
  }));

  const handleAddOrganization = () => {
    const newOrg = {
      id: `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      type: null,
      name: '',
      email: '',
      isNew: true
    };
    setLocalOrganizations((prev) => [newOrg, ...prev]);
  };

  const handleDeleteOrganization = (orgId) => {
    const org = localOrganizations.find((org) => org.id === orgId);
    if (!org.isNew) return; // existing orgs can not be deleted

    setLocalOrganizations((prev) => prev.filter((org) => org.id !== orgId));
  };

  const updateLocalOrg = (orgId, updates) => {
    setLocalOrganizations((prev) => prev.map((org) => (org.id === orgId ? { ...org, ...updates } : org)));
  };

  const handleFieldBlur = (orgId, fieldName) => {
    setTouchedFields((prev) => ({
      ...prev,
      [getFieldTouchKey(orgId, fieldName)]: true
    }));
  };

  const isOrganizationRowEmpty = (org) => !org.type && !trimValue(org.name) && !trimValue(org.email);

  const isOrganizationNameDuplicated = (name) => {
    const trimmedName = trimValue(name);
    if (!trimmedName) return false;
    return localOrganizations.filter((org) => trimValue(org.name) === trimmedName).length > 1;
  };

  const getFieldErrorMessage = (org, fieldName, requireTouched = true) => {
    const isFieldTouched = touchedFields[getFieldTouchKey(org.id, fieldName)];

    if (requireTouched && !isFieldTouched) return '';

    if (isOrganizationRowEmpty(org)) return '';

    if (fieldName === 'type' && !org.type) return '↑ Pflichtfeld';

    if (fieldName === 'name') {
      if (!trimValue(org.name)) return '↑ Pflichtfeld';
      if (isOrganizationNameDuplicated(org.name)) return '↑ Dieser Unternehmensname wird bereits verwendet';
    }

    if (fieldName === 'email') {
      if (!trimValue(org.email)) return '↑ Pflichtfeld';
      if (!EMAIL_FORMAT_REGEX.test(trimValue(org.email))) return '↑ Die Eingabe ist keine gültige E-Mail';
    }

    return '';
  };

  const isOrganizationValid = (org) => {
    return ['type', 'name', 'email'].every((fieldName) => !getFieldErrorMessage(org, fieldName, false));
  };

  const hasInsurance = () => {
    const filledOrgs = localOrganizations.filter((org) => !isOrganizationRowEmpty(org));
    return filledOrgs.some((org) => org.type === 'insurance');
  };

  const processOrganizationsAndSave = (callback, requireInsurance = false) => {
    // Drop empty new rows
    const filteredOrgs = localOrganizations.filter((org) => !(org.isNew && isOrganizationRowEmpty(org)));
    setLocalOrganizations(filteredOrgs);

    const filledOrgs = filteredOrgs.filter((org) => !isOrganizationRowEmpty(org));
    const hasErrors = filledOrgs.some((org) => !isOrganizationValid(org));

    if (hasErrors) {
      setTouchedFields(touchAllFields(filteredOrgs, ['type', 'name', 'email']));
      return;
    }

    if (requireInsurance && !filledOrgs.some((org) => org.type === 'insurance')) {
      return;
    }

    // Filter valid orgs (ignore empty rows)
    const validOrgs = filledOrgs.filter((org) => isOrganizationValid(org));

    // Calculate diff — strip UI-only 'isNew' before passing to saga
    // eslint-disable-next-line no-unused-vars
    const orgsToCreate = validOrgs.filter((org) => org.isNew).map(({ isNew, ...org }) => org);
    const orgsToUpdate = validOrgs.filter((org) => {
      if (org.isNew) return false;
      const initial = initialOrganizationsState.find((initialOrg) => initialOrg.id === org.id);
      return initial && !deepEqual(org, initial);
    });

    // Skip if no changes
    if (orgsToCreate.length === 0 && orgsToUpdate.length === 0) {
      callback?.();
      return;
    }

    // Bulk call (sequential saga)
    bulkEditOrganizations({ orgsToCreate, orgsToUpdate, callback });
  };

  const handleBack = () => {
    processOrganizationsAndSave(() => {
      updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.HOUSING_STOCK });
    });
  };

  const handleNext = () => {
    processOrganizationsAndSave(() => {
      updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.NOTIFICATIONS });
    }, true);
  };

  const isLoading = updateConfigLoading || organizationsLoading || bulkEditOrganizationsLoading;

  return (
    <>
      <OnboardingNavbar loading={isLoading} />
      <OnboardingProgress />
      <OnboardingContent>
        <OnboardingStepWrapper>
          <OnboardingHeaderGroup>
            <OnboardingContentTitle>
              <Heading2>Unternehmen hinzufügen</Heading2>
            </OnboardingContentTitle>
            <OnboardingContentInfo>
              <OnboardingContentGroupText>
                Damit Sie mit verschiedenen Unternehmen, die für den Bestand relevant sind, auf oqio zusammenarbeiten
                können, müssen Sie diese hinzufügen. Sie können beliebig viele verschiedene Unternehmen anlegen.
              </OnboardingContentGroupText>
            </OnboardingContentInfo>
          </OnboardingHeaderGroup>
          <OnboardingContentGroup className="step-organizations action">
            <OnboardingContentGroupTitle>
              <Heading3>Unternehmen</Heading3>
              <OnboardingContentGroupTitleDescription>
                Mindestens eine Versicherung ist erforderlich.
              </OnboardingContentGroupTitleDescription>
            </OnboardingContentGroupTitle>
            <Button type="link" icon={<IconCircledPlus />} onClick={handleAddOrganization}>
              Hinzufügen
            </Button>
          </OnboardingContentGroup>

          <OnboardingContentGroup
            className={classNames('step-organizations', 'content-head', { empty: !localOrganizations?.length })}
          >
            <OnboardingOrganizationListHeader>
              <OnboardingOrganizationListHeaderItem>Art des Unternehmens</OnboardingOrganizationListHeaderItem>
              <OnboardingOrganizationListHeaderItem>Name des Unternehmens</OnboardingOrganizationListHeaderItem>
              <OnboardingOrganizationListHeaderItem>E-Mail</OnboardingOrganizationListHeaderItem>
              <OnboardingOrganizationListHeaderItem></OnboardingOrganizationListHeaderItem>
            </OnboardingOrganizationListHeader>
          </OnboardingContentGroup>

          {localOrganizations && localOrganizations.length > 0 && (
            <OnboardingContentGroup className="step-organizations content">
              {localOrganizations.map((org) => {
                const typeError = getFieldErrorMessage(org, 'type');
                const nameError = getFieldErrorMessage(org, 'name');
                const emailError = getFieldErrorMessage(org, 'email');

                return (
                  <OnboardingOrganizationListRow key={org.id}>
                    <OnboardingFieldWrapper>
                      <Select
                        value={org.type}
                        options={filteredOrganizationTypeOptions}
                        disabled={!org.isNew}
                        placeholder="Unternehmensart wählen"
                        showCheckmark={false}
                        status={typeError ? 'error' : ''}
                        onChange={(value) => {
                          updateLocalOrg(org.id, { type: value });
                          handleFieldBlur(org.id, 'type');
                        }}
                        onBlur={() => handleFieldBlur(org.id, 'type')}
                      />
                      {typeError && <OnboardingFieldErrorText>{typeError}</OnboardingFieldErrorText>}
                    </OnboardingFieldWrapper>
                    <OnboardingFieldWrapper>
                      <Input
                        value={org.name}
                        status={nameError ? 'error' : ''}
                        onChange={(e) => updateLocalOrg(org.id, { name: e.target.value })}
                        onBlur={() => handleFieldBlur(org.id, 'name')}
                      />
                      {nameError && <OnboardingFieldErrorText>{nameError}</OnboardingFieldErrorText>}
                    </OnboardingFieldWrapper>
                    <OnboardingFieldWrapper>
                      <Input
                        value={org.email}
                        status={emailError ? 'error' : ''}
                        onChange={(e) => updateLocalOrg(org.id, { email: e.target.value })}
                        onBlur={() => handleFieldBlur(org.id, 'email')}
                      />
                      {emailError && <OnboardingFieldErrorText>{emailError}</OnboardingFieldErrorText>}
                    </OnboardingFieldWrapper>
                    <Button
                      type="link"
                      danger
                      icon={<IconTrash />}
                      onClick={() => handleDeleteOrganization(org.id)}
                      disabled={!org.isNew}
                    />
                  </OnboardingOrganizationListRow>
                );
              })}
            </OnboardingContentGroup>
          )}
        </OnboardingStepWrapper>
        <OnboardingActions
          onBack={handleBack}
          onNext={handleNext}
          loading={isLoading}
          nextHint={!hasInsurance() && 'Legen Sie mindestens eine Versicherung an, um fortzufahren.'}
        />
      </OnboardingContent>
    </>
  );
};

const mapStateToProps = (state) => ({
  updateConfigLoading: selectUpdateConfigLoading(state),
  organizations: selectOrganizationsForOnboarding(state),
  organizationsLoading: selectOrganizationsLoading(state),
  bulkEditOrganizationsLoading: selectBulkEditOrganizationsLoading(state)
});

const mapDispatchToProps = {
  updateConfigOnboardingSettings,
  getOrganizations,
  bulkEditOrganizations
};

export const OnboardingStepOrganizations = connect(
  mapStateToProps,
  mapDispatchToProps
)(OnboardingStepOrganizationsComponent);
